// What a "complete" job means, and how a job's margin is shown when its numbers are not complete.
// Pure functions: no screen code, no database. Money is compared in whole cents (src/money.js).
//
// A job is complete when it has all of these:
//   - revenue above $0          (otherwise: "No revenue recorded")
//   - costs above $0            (otherwise: "No costs recorded")
//   - labor above $0            (otherwise: "No labor recorded") -- only when the business type counts missing
//                                labor (rule.missingLaborCounts, see src/businessPreset.js; default true = Trades)
//   - no pending expenses that point at it  (otherwise: "2 expenses may belong to this job")
// Anything else is incomplete. Revenue, costs or labor that nets out at $0 or below (for example a credit
// memo bigger than the invoices) count as missing, because there is no real amount to build a margin on.
//
// Follow-up (not built here): a way to confirm "this job really had no labor", which will clear the
// labor flag for jobs that truly have none.

import { toWholeCents } from "./money";
import { marginPercent } from "./aiCharts";

const cents = (v) => toWholeCents(Number(v) || 0);

// Pending expenses (not yet assigned) that suggest this job. Each item looks like the app's inbox items:
// { id, amount, suggestedJob }. The same id is counted once. Returns { count, totalCents }.
export function pendingForJob(jobId, pendingItems) {
  const seen = new Set();
  let count = 0, totalCents = 0;
  for (const it of pendingItems || []) {
    if (!it || it.suggestedJob == null || it.suggestedJob !== jobId) continue;
    if (it.id != null) {
      if (seen.has(it.id)) continue;
      seen.add(it.id);
    }
    count += 1;
    totalCents += cents(it.amount);
  }
  return { count, totalCents };
}

// job: { id, revenue, costs, laborCost } in dollars. pendingItems: inbox items still waiting for a job.
// rule: { missingLaborCounts, noCostsWithholds, noLaborText } (the first two default true, the text "No labor recorded"; see completenessRule in src/businessPreset.js).
// missingLaborCounts false: a job with no labor is NOT incomplete for that reason and its margin is not withheld
// for it. flags.noLabor still tells the plain fact (no labor recorded).
// noCostsWithholds false (Solo services): a job with revenue and no costs is only a soft note. Its real margin is
// shown, it stays "complete" (so it is not counted in "jobs missing numbers", the incomplete alert group or the AI
// banner), and softNotes holds "No costs recorded" so the job's chip can still say it.
// Returns { flags, pendingCount, pendingCents, reasons, softNotes, status }.
//   flags: { noRevenue, noCosts, noLabor, laborCounts, noCostsWithholds, hasPendingSuggestions }
//   reasons: short owner-facing sentences, in a fixed order. When there are no costs at all, "No labor
//     recorded" is left out (it would repeat "No costs recorded"), but the noLabor flag is still true.
//   status: "complete" or "incomplete".
export function jobCompleteness(job, pendingItems, rule) {
  const j = job || {};
  const laborCounts = !rule || rule.missingLaborCounts !== false;
  const noCostsWithholds = !rule || rule.noCostsWithholds !== false;
  const noLaborText = (rule && rule.noLaborText) || "No labor recorded"; // Services with a team say "No contractor costs recorded"
  const flags = {
    noRevenue: cents(j.revenue) <= 0,
    noCosts: cents(j.costs) <= 0,
    noLabor: cents(j.laborCost) <= 0,
    laborCounts,
    noCostsWithholds,
    hasPendingSuggestions: false,
  };
  const pending = pendingForJob(j.id, pendingItems);
  flags.hasPendingSuggestions = pending.count > 0;

  const reasons = [];
  if (flags.noRevenue) reasons.push("No revenue recorded");
  const softNotes = [];
  if (flags.noCosts) {
    if (noCostsWithholds) reasons.push("No costs recorded");
    else if (!flags.noRevenue) softNotes.push("No costs recorded"); // Solo services: soft note only, and only once there is revenue
  } else if (flags.noLabor && laborCounts) reasons.push(noLaborText);
  if (flags.hasPendingSuggestions) {
    reasons.push(pending.count === 1 ? "1 expense may belong to this job" : `${pending.count} expenses may belong to this job`);
  }
  return {
    flags,
    pendingCount: pending.count,
    pendingCents: pending.totalCents,
    reasons,
    softNotes,
    noLaborText,
    status: reasons.length === 0 ? "complete" : "incomplete",
  };
}

// True when the job's margin would look better than it really is: no costs, or no labor where labor counts.
// flags is the flags object from jobCompleteness (laborCounts missing means labor counts).
export function marginNeedsMoreData(flags) {
  const f = flags || {};
  return (!!f.noCosts && f.noCostsWithholds !== false) || (!!f.noLabor && f.laborCounts !== false);
}

// How to show one job's margin. completeness is the result of jobCompleteness for the same job.
// Returns { kind, text, value, sortValue }:
//   kind "none"       no revenue (or revenue at or below $0): there is no margin to compute. text "—".
//                     A job with costs and no revenue is a loss; its Profit figure shows that with a minus sign.
//   kind "incomplete" revenue, a profit above $0, but no costs or no labor recorded. The number would look
//                     better than the job really is, so text is "Incomplete" and the number is withheld.
//   kind "number"     everything else, e.g. "42.5%" or "-12.0%". A job at or below break-even keeps its
//                     real number even with labor missing, because adding the missing cost can only lower it.
// value is the number when there is revenue (even when withheld), else null. sortValue is the number only
// for kind "number", else null, so "—" and "Incomplete" never rank as a good margin.
export function marginView(job, completeness) {
  const j = job || {};
  const c = completeness || jobCompleteness(j, []);
  const revC = cents(j.revenue);
  if (revC <= 0) return { kind: "none", text: "—", value: null, sortValue: null };
  const profitC = revC - cents(j.costs);
  const value = marginPercent(profitC, revC);
  if (marginNeedsMoreData(c.flags) && profitC > 0) {
    return { kind: "incomplete", text: "Incomplete", value, sortValue: null };
  }
  return { kind: "number", text: `${value.toFixed(1)}%`, value, sortValue: value };
}

// The words on the small chip next to a job. "Incomplete" is used ONLY when the margin is withheld
// (margin.kind === "incomplete"), because then the chip stands in for the margin. When a real margin or a dash
// is shown, the chip names what is wrong instead, so a number that is fine but has expenses waiting reads
// "2 expenses to review", not "Incomplete". If several things are wrong, the chip names the first, in this order:
// expenses to review, no revenue, no costs, no labor. (The hover text lists them all.)
// completeness: jobCompleteness() result. margin: marginView() result for the same job.
// Returns the text, or null when the job is complete. A complete job with a soft note (Solo services, revenue and
// no costs) gets that note as its chip.
export function chipLabel(completeness, margin) {
  const c = completeness;
  if (c && c.status === "complete" && c.softNotes && c.softNotes.length > 0) return c.softNotes[0];
  if (!c || c.status !== "incomplete") return null;
  if (!margin || margin.kind === "incomplete") return "Incomplete";
  if (c.pendingCount > 0) return c.pendingCount === 1 ? "1 expense to review" : `${c.pendingCount} expenses to review`;
  if (c.flags.noRevenue) return "No revenue recorded";
  if (c.flags.noCosts) return "No costs recorded";
  return c.noLaborText || "No labor recorded";
}

// Sort helper for the margin column. views are marginView results; dir is "desc" (best first) or "asc".
// Three tiers, in this order for both directions: real numbers (by value), then "Incomplete" jobs (by their
// withheld value, same direction), then "—" (no revenue). "Incomplete" never ranks above a real number.
export function compareMargin(a, b, dir) {
  const tier = (v) => (v && v.kind === "incomplete" ? 1 : v && v.sortValue !== null && v.sortValue !== undefined ? 0 : 2);
  const rank = (v) => (tier(v) === 0 ? v.sortValue : tier(v) === 1 ? v.value : null);
  const ta = tier(a), tb = tier(b);
  if (ta !== tb) return ta - tb;
  if (ta === 2) return 0;
  return dir === "asc" ? rank(a) - rank(b) : rank(b) - rank(a);
}

// Counts for a group of jobs (a client, or the whole dashboard). pendingItems as for jobCompleteness.
export function completenessSummary(jobs, pendingItems, rule) {
  const list = jobs || [];
  const incomplete = list.filter((j) => jobCompleteness(j, pendingItems, rule).status === "incomplete");
  return { total: list.length, incompleteCount: incomplete.length, incompleteIds: incomplete.map((j) => j.id) };
}
